import React, { useCallback, useMemo, useState } from "react";
import Theme from "../utils/theme";
import { ThemeProvider } from "@mui/material/styles";
import {
    Avatar,
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    Button,
    MenuItem,
    Tooltip,
    ListItemIcon,
    Divider,
    // Slide,
} from "@mui/material";
import { PersonAdd, Settings, Logout as LogoutIcon, Person, Cookie } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import type { RootState } from "../store";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import topBarProps from "../utils/topBarProps";
import TopbarSearch from "./TopbarSearch";
import { logout } from "../store/slices/auth";
import type { ITopBarProps } from "../utils/topBarProps";

interface IMenuStateType {
    anchorEl: null | HTMLElement;
    menuName: string | null;
}

const Topbar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const authState = useSelector((state: RootState) => state.auth);
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const isGuest = !authState.account;
    const props = topBarProps;

    const [menuState, setMenuState] = useState<IMenuStateType>({ anchorEl: null, menuName: null });

    const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, menuName: string) => {
        setMenuState({ anchorEl: event.currentTarget, menuName });
    }, []);

    const handleCloseMenu = useCallback(() => {
        setMenuState({ anchorEl: null, menuName: null });
    }, []);

    const handleMenuToggle = useCallback(
        (event: React.MouseEvent<HTMLElement>, page: ITopBarProps) => {
            if (page.children.length === 0) {
                handleCloseMenu();
            } else {
                handleOpenMenu(event, page.href);
            }
        },
        [handleCloseMenu, handleOpenMenu]
    );

    const goToPage = useCallback(
        (route: string) => {
            handleCloseMenu();
            navigate(route);
        },
        [navigate, handleCloseMenu]
    );

    const handleLogout = useCallback(() => {
        dispatch(logout());
        navigate("/login");
    }, [dispatch, navigate]);

    const hasMenuItems = useMemo(
        () =>
            (menuState.menuName === "appbar-menu" && props.pages.length > 0) ||
            menuState.menuName === "account" ||
            props.pages.some((page) => page.href === menuState.menuName && page.children?.length),
        [menuState.menuName, props.pages]
    );

    const renderAccountItems = useMemo(
        () => (
            <Box>
                {!isGuest ? (
                    <>
                        <MenuItem onClick={() => goToPage("/profile")}>
                            <ListItemIcon>
                                <Person fontSize="small" />
                            </ListItemIcon>{" "}
                            Profile
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => goToPage("/settings")}>
                            <ListItemIcon>
                                <Settings fontSize="small" />
                            </ListItemIcon>{" "}
                            Settings
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <LogoutIcon fontSize="small" />
                            </ListItemIcon>{" "}
                            Logout
                        </MenuItem>
                    </>
                ) : (
                    <MenuItem onClick={() => goToPage("/login")}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>{" "}
                        Login
                    </MenuItem>
                )}
            </Box>
        ),
        [isGuest, handleCloseMenu, goToPage, handleLogout]
    );

    const renderPageChildren = useMemo(() => {
        const matchedPage = props.pages.find((page) => page.href === menuState.menuName);
        if (!matchedPage || !matchedPage.children?.length) return null;

        return (
            <Box>
                {matchedPage.children.map((child) => (
                    <MenuItem
                        key={child.href}
                        component="a"
                        href={child.href}
                        onClick={(e: any) => handleMenuToggle(e, { ...child, children: [], userOnly: matchedPage.userOnly })}
                    >
                        {child.icon !== undefined && (
                            <ListItemIcon>
                                <Box
                                    component="img"
                                    src={child.icon}
                                    alt={child.label}
                                    sx={{
                                        borderRadius: 5,
                                        width: 30,
                                        height: 30,
                                    }}
                                />
                            </ListItemIcon>
                        )}
                        {child.label}
                    </MenuItem>
                ))}
            </Box>
        );
    }, [props.pages, menuState.menuName, handleMenuToggle]);

    const renderMaybeTooltipButton = useCallback(
        (page: any, disabled: boolean) => {
            if (disabled) {
                return (
                    <Tooltip key={page.href} placement="bottom" title="Please log in to access your library.">
                        <Box component="span" sx={{ display: "inline-block" }}>
                            <Button
                                component={page.children.length === 0 ? "a" : "div"}
                                href={page.href}
                                disabled
                                onClick={(e: any) => handleMenuToggle(e, page)}
                                sx={{
                                    my: 2,
                                    color: "white",
                                    display: "block",
                                    marginLeft: 3,
                                }}
                            >
                                {page.label}
                            </Button>
                        </Box>
                    </Tooltip>
                );
            }

            return (
                <Button
                    key={page.href}
                    component={page.children.length === 0 ? "a" : "div"}
                    href={page.href}
                    onClick={(e) => handleMenuToggle(e, page)}
                    sx={{
                        my: 2,
                        color: "white",
                        display: "block",
                        marginLeft: 3,
                    }}
                >
                    {page.label}
                </Button>
            );
        },
        [handleMenuToggle]
    );

    const renderAppBarItems = useMemo(
        () => (
            <Box>
                <MenuItem
                    component="a"
                    href="/generate"
                    onClick={(e) => {
                        handleMenuToggle(e, { label: "Start Cooking", href: "/generate", userOnly: false, children: [] });
                        handleCloseMenu();
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "18px",
                            color: theme.palette.primary.main,
                            textAlign: "center",
                            borderRadius: 5,
                            border: "2px solid " + theme.palette.primary.main,
                            p: 1,
                        }}
                    >
                        Start Cooking
                    </Typography>
                </MenuItem>
                {props.pages.map((page) => {
                    if (page.children.length === 0)
                        return (
                            <MenuItem
                                key={page.href}
                                component={page.children.length === 0 ? "a" : "div"}
                                href={page.href}
                                disabled={page.userOnly && isGuest}
                                onClick={(e) => {
                                    handleMenuToggle(e, page);
                                    handleCloseMenu();
                                }}
                            >
                                <Typography sx={{ textAlign: "center" }}>{page.label}</Typography>
                            </MenuItem>
                        );
                    else
                        return page.children.map((child) => (
                            <MenuItem
                                key={child.href}
                                component="a"
                                href={child.href}
                                disabled={page.userOnly && isGuest}
                                onClick={(e) => {
                                    handleMenuToggle(e, { ...child, children: [], userOnly: page.userOnly });
                                    handleCloseMenu();
                                }}
                            >
                                <Typography sx={{ textAlign: "center" }}>{child.label}</Typography>
                            </MenuItem>
                        ));
                })}
            </Box>
        ),
        [theme.palette.primary.main, props.pages, handleMenuToggle, handleCloseMenu, isGuest]
    );

    const renderTopbarItems = useMemo(
        () => props.pages.map((page) => renderMaybeTooltipButton(page, Boolean(page.userOnly && isGuest))),
        [isGuest, props.pages, renderMaybeTooltipButton]
    );

    return (
        <ThemeProvider theme={theme}>
            {/* <Box sx={{ height: "65px" }} /> */}
            <AppBar sx={{ position: "relative", px: 1 }}>
                <Toolbar
                    disableGutters
                    sx={{
                        height: "65px",
                    }}
                >
                    {/* Mobile Left Menu */}
                    <Box
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    paddingRight: "5px",
                                    height: "40px",
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/images/coocoo.png"
                                    onClick={() => navigate("/")}
                                    alt="Logo"
                                    sx={{ borderRadius: 2, height: 40, width: 40, cursor: "pointer" }}
                                />
                            </Box>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <IconButton onClick={(e) => handleOpenMenu(e, "appbar-menu")} color="inherit">
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Desktop Navigation */}
                    <Box
                        sx={{
                            display: {
                                xs: "none",
                                md: "flex",
                            },
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: "5px",
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/coocoo.png"
                                onClick={() => navigate("/")}
                                alt="Logo"
                                sx={{ borderRadius: 2, height: 40, width: 40, mr: 1, cursor: "pointer" }}
                            />
                        </Box>
                        <Divider orientation="vertical" variant="middle" flexItem />
                        <Box
                            sx={{
                                display: "flex",
                                ml: 1,
                            }}
                        >
                            {renderTopbarItems}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            p: 1,
                            display: "flex",
                            flexGrow: 1,
                            width: "100%",
                        }}
                    >
                        <TopbarSearch />
                    </Box>

                    {/* Right Account Section */}
                    <Box
                        sx={{
                            display: "flex",
                            flexGrow: 0,
                            flexDirection: "row",
                        }}
                    >
                        {/* Desktop Start Cooking Button */}
                        <Box sx={{ display: { xs: "none", md: "flex" } }}>
                            <Box
                                component="a"
                                href="/generate"
                                sx={{
                                    ml: 2,
                                    color: "white",
                                    width: "125px",
                                    userSelect: "none",
                                    textDecoration: "none",
                                    borderRadius: "20px",
                                    boxShadow: 3,
                                    minWidth: 0,
                                    backgroundColor: theme.palette.primary.light,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography>Start Cooking</Typography>
                            </Box>
                        </Box>
                        <Tooltip title="Account settings">
                            <IconButton onClick={(e) => handleOpenMenu(e, "account")} size="small" sx={{ ml: 2 }}>
                                <Avatar
                                    src={authState.account?.image}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>

                {menuState.anchorEl && menuState.menuName && hasMenuItems && (
                    <Menu
                        anchorEl={menuState.anchorEl}
                        open
                        onClose={handleCloseMenu}
                        keepMounted
                        slotProps={{
                            paper: {
                                elevation: 0,
                                sx: {
                                    overflow: "visible",
                                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                    mt: 1.5,
                                    "& .MuiAvatar-root": {
                                        width: 32,
                                        height: 32,
                                        ml: -0.5,
                                        mr: 1,
                                    },
                                    "&::before": {
                                        content: '""',
                                        display: "block",
                                        position: "absolute",
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: "background.paper",
                                        transform: "translateY(-50%) rotate(45deg)",
                                        zIndex: 0,
                                    },
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                        {menuState.menuName === "appbar-menu" && renderAppBarItems}
                        {menuState.menuName === "account" && renderAccountItems}
                        {renderPageChildren}
                    </Menu>
                )}
            </AppBar>
            {/* </HideOnScroll> */}
        </ThemeProvider>
    );
};

export default Topbar;
